import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { sendHomepageDemoEmail } from "../../utils/emailService";
import { 
  Sun, Zap, Building2, Activity, Moon, Lock, MapPin, ArrowRight, 
  Wind, CheckCircle, Play, TrendingUp, Shield, Users, Globe,
  BarChart3, Settings, Target, Award, Star, Clock, Eye, ChevronLeft, ChevronRight, Calendar,
  Leaf, RefreshCw, Square, PlusCircle, Grid2x2, Sparkles
} from "lucide-react";
import CountUp from "react-countup";
import Layout from "../../components/layout/Layout";
import Footer from "../../components/layout/Footer";
import { useHoverEffect } from "../../hooks/useHoverEffect";
import { useServiceCardHover } from "../../hooks/useServiceCardHover";
import { useTheme } from "../../components/ThemeContext.tsx";
import { HeroImage } from "../../components/ui/ImageComponent";
import HeroCarousel, { HERO_CAROUSEL_SETS, DYNAMIC_CAROUSEL_CONFIGS } from "../../components/ui/HeroCarousel";
import { IMAGE_SETS } from "../../constants/images";
import backendApi from "../../services/backendApi";


// Import existing components

// Asset Lifecycle Management Slider Component
const AssetLifecycleSlider = ({ isDarkMode }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const assetCards = [
    {
      number: "01",
      title: "Gain Asset Performance Visibility",
      description: "In one view, gain highlights of assets that need technical attention and quickly understand the overall performance of each plant",
      icon: Eye,
      gradient: "from-blue-600 to-blue-700",
      hoverGradient: "from-blue-700 to-gray-600"
    },
    {
      number: "02", 
      title: "Address Underperformance",
      description: "Identify and resolve performance issues with AI-driven insights and automated corrective action recommendations",
      icon: TrendingUp,
      gradient: "from-gray-600 to-gray-700",
      hoverGradient: "from-gray-700 to-blue-600"
    },
    {
      number: "03",
      title: "Optimize Energy Generation",
      description: "Maximize efficiency and reduce downtime with predictive analytics and real-time optimization algorithms",
      icon: Target,
      gradient: "from-blue-500 to-blue-600",
      hoverGradient: "from-blue-600 to-gray-600"
    },
    {
      number: "04",
      title: "Predictive Maintenance",
      description: "Leverage AI-powered predictive analytics to anticipate equipment failures and schedule maintenance proactively",
      icon: Shield,
      gradient: "from-gray-500 to-gray-600",
      hoverGradient: "from-gray-600 to-blue-600"
    },
    {
      number: "05",
      title: "Real-time Monitoring",
      description: "Monitor asset performance in real-time with advanced SCADA systems and IoT sensors for instant insights",
      icon: Activity,
      gradient: "from-blue-400 to-blue-500",
      hoverGradient: "from-blue-500 to-gray-600"
    },
    {
      number: "06",
      title: "Energy Trading Optimization",
      description: "Optimize energy trading strategies with market intelligence and automated bidding algorithms",
      icon: BarChart3,
      gradient: "from-gray-400 to-gray-500",
      hoverGradient: "from-gray-500 to-blue-600"
    }
  ];

  const cardsPerSlide = 3;
  const totalSlides = Math.ceil(assetCards.length / cardsPerSlide);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const getCurrentCards = () => {
    const startIndex = currentSlide * cardsPerSlide;
    return assetCards.slice(startIndex, startIndex + cardsPerSlide);
  };

  return (
    <div className="relative">
      {/* Simple Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {assetCards.map((process, index) => (
          <div
            key={index}
            className={`rounded-xl p-6 shadow-sm border transition-all duration-300 ${
              isDarkMode 
                ? 'bg-slate-800/90 border-slate-700 hover:shadow-slate-900/50' 
                : 'bg-white border-gray-100 hover:shadow-md'
            }`}
          >
                  
                  {/* Icon */}
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{backgroundColor: '#1C4D88'}}>
              <process.icon className="text-white" size={20} />
                  </div>
                  
                  {/* Content */}
            <div className="space-y-3">
              <h3 className={`text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {process.title}
                    </h3>
              <p className={`text-sm font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
                      {process.description}
                    </p>
                  </div>
                </div>
        ))}
      </div>
    </div>
  );
};

// Dynamic Data Structures
const heroData = {
  badge: {
    icon: "/assets/working-img/bale.png",
    text: "AxonMetric Renewables",
    gradient: "from-blue-500 to-cyan-500"
  },
  headline: {
    main: "Maximize",
    subtitle: "Efficiency And Reliability",
    description: "Of Renewable Energy Assets",
    gradient: "from-blue-600 via-purple-600 to-cyan-600"
  },
  description: "Advanced analytics and AI-driven insights for wind, solar, and battery storage systems.",
  features: [
    { icon: CheckCircle, text: "Asset Performance Visibility", color: "#1C4D88" },
    { icon: CheckCircle, text: "Address Underperformance", color: "#1C4D88" },
    { icon: CheckCircle, text: "Reduce Downtime and Costs", color: "#1C4D88" }
  ],
  cta: {
    text: "Explore Platform",
    link: "#site-select",
    gradient: "from-blue-600 to-cyan-600"
  }
};

const statsData = {
  main: {
    value: 3.0,
    unit: "GW",
    label: "Total Energy Production",
    gradient: "from-blue-600 to-cyan-600"
  },
  plants: [
    {
      type: "Solar Plants",
      count: 11,
      capacity: "2.6+ GW",
      icon: Sun,
      color: "#6B7280",
      gradient: "from-gray-500 to-gray-600"
    },
    {
      type: "Wind Farms",
      count: 2,
      capacity: "0.4+ GW",
      icon: Wind,
      color: "#6B7280",
      gradient: "from-gray-500 to-gray-600"
    }
  ],
  quickStats: [
    { value: 13, label: "Total Sites", color: "#0084CF" },
    { value: "24/7", label: "Monitoring", color: "#1C4D88" },
    { value: 2017, label: "Since", color: "#7F9CC8" }
  ],
  offerings: "Performance, Forecasting, RTC Optimization, Energy Trading"
};

const servicesData = [
  {
    id: 1,
    title: "Platform Exploration",
    description: "Discover comprehensive analytics and AI-driven insights for wind, solar, and battery storage systems to optimize your renewable energy portfolio.",
    icon: Globe,
    gradient: "from-blue-600 to-blue-700",
    hoverGradient: "from-blue-700 to-gray-600"
  },
  {
    id: 2,
    title: "Real-time Analytics",
    description: "Monitor your renewable energy assets in real-time with advanced analytics and predictive insights.",
    icon: BarChart3,
    gradient: "from-gray-600 to-gray-700",
    hoverGradient: "from-gray-700 to-blue-600"
  },
  {
    id: 3,
    title: "Performance Optimization",
    description: "Optimize energy generation and reduce downtime with AI-driven recommendations.",
    icon: Target,
    gradient: "from-blue-500 to-blue-600",
    hoverGradient: "from-blue-600 to-gray-600"
  }
];

const featuresData = [
  {
    icon: TrendingUp,
    title: "Advanced Analytics",
    description: "AI-driven insights for optimal performance",
    gradient: "from-blue-600 to-blue-700"
  },
  {
    icon: Shield,
    title: "Reliability & Security",
    description: "Enterprise-grade security and uptime",
    gradient: "from-gray-600 to-gray-700"
  },
  {
    icon: Users,
    title: "Team Collaboration",
    description: "Seamless team coordination and reporting",
    gradient: "from-blue-500 to-blue-600"
  },
  {
    icon: Clock,
    title: "24/7 Monitoring",
    description: "Round-the-clock asset surveillance",
    gradient: "from-gray-500 to-gray-600"
  }
];

const siteList = [
  { name: "Axon", fullName: "Axon", capacity: "250 MW" },
  { name: "ARPPL", fullName: "Ayana Renewable Power Private Limited", capacity: "200 MW" },
  { name: "ARPOPL", fullName: "Ayana Power One Private Limited", capacity: "220 MW" },
  { name: "ARPTPL", fullName: "Ayana Power Three Private Limited", capacity: "160 MW" },
  { name: "ASEPL", fullName: "Ayana Solar Energy Private Limited", capacity: "175 MW" },
  { name: "BRPPL", fullName: "Bhadla Renewable Power Private Limited", capacity: "180 MW" },
  { name: "TSPPL", fullName: "Tirunelvel Solar Power Private Limited", capacity: "240 MW" },
  { name: "IRPL", fullName: "IRCON Renewable Power Private Limited", capacity: "190 MW" },
  { name: "TSPPL2", fullName: "Tungabhadra Solar Parks Private Limited", capacity: "210 MW" },
  { name: "ASPPL", fullName: "Anantapur Solar Parks Private Limited", capacity: "185 MW" }
];

const FadeDown = ({ delay = 0, className, children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: -20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const StaggeredFade = ({ text, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const letters = text.split("");

  return (
    <h1 ref={ref} className={className} style={{ whiteSpace: "normal", wordBreak: "normal" }}>
      {letters.map((char, i) => (
        <motion.span
          key={`hero-char-${i}`}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3, delay: i * 0.03 }}
        >
          {char}
        </motion.span>
      ))}
    </h1>
  );
};

const NewHomePage = () => {
  const navigate = useNavigate();
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [useVideoBackground, setUseVideoBackground] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const heroVideos = [
    // { src: "/vid/hero.mp4", title: "Renewable Assets Rajasthan", location: "Rajasthan", country: "India" },
    // { src: "/vid/hero2.mp4", title: "Wind Energy Gujarat", location: "Gujarat", country: "India" },
    { src: "/vid/hero3.mp4", title: "Clean Power Maharashtra", location: "Rajasthan", country: "India" },
    // { src: "/vid/hero4.mp4", title: "Renewables Karnataka", location: "Karnataka", country: "India" },
    { src: "/vid/hero5.mp4", title: "Solar Farm Tamil Nadu", location: "Gujarat", country: "India" },
    { src: "/vid/hero6.mp4", title: "Clean Grid Madhya Pradesh", location: "Madhya Pradesh", country: "India" },
    { src: "/vid/hero7.mp4", title: "RE Andhra Pradesh", location: "Andhra Pradesh", country: "India" },
    { src: "/vid/hero8.mp4", title: "Turbine Park Telangana", location: "Telangana", country: "India" },
    // { src: "/vid/hero9.mp4", title: "Energy Hub Punjab", location: "Punjab", country: "India" },
    { src: "/vid/hero10.mp4", title: "Solar Power Haryana", location: "Haryana", country: "India" }
  ];

  const handleVideoEnded = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % heroVideos.length);
  };
  const { isDarkMode } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const marqueeContentRef = useRef(null);
  const [marqueeDurationSec, setMarqueeDurationSec] = useState(60);
  const [newsItems, setNewsItems] = useState([
    { title: "India's Renewable Energy Capacity Crosses 150 GW Mark", url: "https://mnre.gov.in/" },
    { title: "Adani Green Energy operationalizes 12,000 MW of renewable portfolio", url: "https://www.adanigreenenergy.com/" },
    { title: "Tata Power to invest ₹70,000 crore in green energy projects in Rajasthan", url: "https://www.tatapower.com/" },
    { title: "SECI invites bids for 1200 MW ISTS-connected wind-solar hybrid power projects", url: "https://www.seci.co.in/" },
    { title: "Global solar PV installations expected to reach 400 GW in 2026", url: "https://www.cleantechnica.com/" }
  ]);

  useEffect(() => {
    let isMounted = true;
    const fetchNews = async () => {
      try {
        const response = await backendApi.getNewsTicker();
        if (isMounted && response && response.success && response.data && response.data.length > 0) {
          setNewsItems(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch news ticker items:", err);
      }
    };
    fetchNews();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const tickerPxPerSecond = 45;
    const node = marqueeContentRef.current;
    if (!node) return;

    const updateDuration = () => {
      const contentWidth = node.scrollWidth;
      if (!contentWidth) return;
      const nextDuration = Math.max(contentWidth / tickerPxPerSecond, 22);
      setMarqueeDurationSec((prev) => (Math.abs(prev - nextDuration) < 0.5 ? prev : nextDuration));
    };

    updateDuration();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateDuration);
      ro.observe(node);
    }

    window.addEventListener("resize", updateDuration);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", updateDuration);
    };
  }, [newsItems]);


  // Simple carousel images
  const carouselImages = [
    
    '/assets/img/general/windmills-1838788_1280.jpg',
    '/assets/img/general/wind-power-generation-8370867_1280.jpg',
    '/assets/img/general/energy-2302001_1280.jpg',
    '/assets/img/general/solar-2376233_1280.jpg',
    '/assets/img/general/pinwheels-6535595_1280.jpg',
    '/assets/img/general/photovoltaic-2814504_1280.jpg'
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % featuresData.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Carousel auto-advance
  useEffect(() => {
    const carouselInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 6000);
    return () => clearInterval(carouselInterval);
  }, [carouselImages.length]);

  useEffect(() => {
    const handleResize = () => {
      setUseVideoBackground(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    handleMouseEnter,
    handleMouseLeave,
    getCardStyles,
    getBackgroundPatternStyles,
    getAccentLineStyles,
    getNumberBadgeStyles,
    getHoverIconStyles,
    getTitleStyles,
    getTextStyles,
  } = useHoverEffect();

  const {
    handleMouseEnter: handleServiceMouseEnter,
    handleMouseLeave: handleServiceMouseLeave,
    getCardStyles: getServiceCardStyles,
    getBackgroundPatternStyles: getServiceBackgroundPatternStyles,
    getAccentLineStyles: getServiceAccentLineStyles,
    getIconStyles: getServiceIconStyles,
    getTitleStyles: getServiceTitleStyles,
    getTextStyles: getServiceTextStyles,
    getReadMoreStyles: getServiceReadMoreStyles,
    getArrowStyles: getServiceArrowStyles,
    getHoverIconStyles: getServiceHoverIconStyles,
  } = useServiceCardHover();

  // Form submission handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Get form data
      const formData = new FormData(e.target);
      const data = {
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
        email: formData.get('email'),
        company: formData.get('company'),
        energyCapacity: formData.get('energyCapacity')
      };
      
      // Send email using EmailJS
      await sendHomepageDemoEmail(data);
      
      // Success
      setIsSubmitting(false);
      alert('Thank you for your interest! Our team will contact you within 24 hours.');
      e.target.reset();
    } catch (error) {
      // Error handling
      setIsSubmitting(false);
      alert('There was an error submitting your request. Please try again or contact us directly.');
      console.error('Form submission error:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Layout header={6}>
      {/* Page-wide background wrapper */}
      <div className={`${isDarkMode ? 'bg-[#0B1120] text-slate-100' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-900'} min-h-screen transition-colors duration-300`}>
        <section className={`relative h-screen overflow-hidden ${isDarkMode ? 'bg-[#0B1120]' : 'bg-[#F5F3EE]'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
          <AnimatePresence mode="wait">
            <motion.video
              key={currentVideoIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              className="absolute inset-0 z-0 h-full w-full object-cover"
              autoPlay
              loop={false}
              muted
              playsInline
              onEnded={handleVideoEnded}
              src={heroVideos[currentVideoIndex].src}
            />
          </AnimatePresence>


          <div className="relative z-10 flex h-full flex-col">
            <div className="flex flex-1 flex-col items-center justify-center px-4 md:px-8">
              {/* <FadeDown className="mb-3" delay={0.1}>
                <div className="rounded-full border border-black/20 bg-white/20 px-3 py-2 text-xs text-gray-800 backdrop-blur-sm md:text-sm">
                  <span className="inline md:hidden">Power Innovation for a Sustainable Future</span>
                  <span className="hidden md:inline">Delivering Power Innovation for a Sustainable Future</span>
                </div>
              </FadeDown> */}

              <StaggeredFade
                text="Maximize Efficiency And Reliability Of Renewable Energy Assets"
                className="mb-3 max-w-5xl text-center text-3xl font-normal leading-tight text-white sm:text-4xl md:mb-4 md:text-5xl lg:text-6xl"
              />

              {/* <FadeDown className="mb-4 max-w-3xl text-center text-sm text-gray-600 md:mb-5 md:text-base lg:text-lg" delay={0.5}>
                Sustainable Energy Platform. Engineering, deploying, and servicing solar arrays for homes, businesses, and large-scale operations worldwide.
              </FadeDown> */}

              {/* <FadeDown delay={0.7}>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => scrollToSection('image-gallery')}
                    className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#3C684D] to-[#4A7144] px-4 py-2 text-white md:px-5 md:py-2.5"
                  >
                    <Leaf size={16} />
                    <span className="text-sm font-medium">Explore Options</span>
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-full md:h-8 md:w-8"
                      style={{ background: "linear-gradient(59deg, #567A5E 0%, #78A873 100%)" }}
                    >
                      <Play size={12} fill="currentColor" className="text-white" />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => scrollToSection('expert-consultation')}
                    className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-gray-700 md:px-5 md:py-2.5"
                  >
                    <span className="text-sm font-medium">Start Network</span>
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-full md:h-8 md:w-8"
                      style={{ background: "linear-gradient(59deg, #EEEEEE 0%, #CBCBCB 100%)" }}
                    >
                      <ArrowRight size={14} className="text-black" />
                    </span>
                  </button>
                </div>
              </FadeDown> */}
            </div>

            <div className="absolute bottom-24 left-8 z-10 hidden md:block">
              <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg shadow-md transition-colors ${
                isDarkMode ? 'bg-slate-900/90 border border-slate-700/80 backdrop-blur-sm' : 'bg-white'
              }`}>
                <MapPin size={18} className={isDarkMode ? 'text-emerald-400' : 'text-[#4A7C5A]'} />
              </div>
              <p className="text-sm font-medium text-white">{heroVideos[currentVideoIndex].location}</p>
              <p className="text-sm text-white/80">{heroVideos[currentVideoIndex].country}</p>
            </div>

            {/* Video Background Carousel Indicators */}
            <div className="absolute bottom-24 right-8 z-20 flex items-center gap-3">
              {heroVideos.map((vid, idx) => (
                <button
                  key={`vid-indicator-${idx}`}
                  type="button"
                  onClick={() => setCurrentVideoIndex(idx)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    currentVideoIndex === idx
                      ? 'w-8 bg-white'
                      : 'w-2.5 bg-white/30 hover:bg-white/50'
                  }`}
                  title={vid.title}
                  aria-label={`Switch to video background ${idx + 1}`}
                />
              ))}
            </div>

            {/* <div className="absolute bottom-20 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 md:bottom-24">
              <button className="liquid-glass flex h-9 w-9 items-center justify-center rounded-full md:h-10 md:w-10">
                <Play size={14} fill="currentColor" className="text-white" />
              </button>
              <span className="text-xs font-medium text-white md:text-sm">Clean Power System</span>
            </div> */}

            <div className="absolute bottom-5 right-14 z-10 hidden lg:block">
              <div className="mb-2 h-16 w-28 relative">
                <img
                  src="/assets/img/avatars/avatar1.png"
                  alt="User avatar left"
                  className={`absolute left-0 h-10 w-10 rounded-full object-cover border-2 shadow-md ${isDarkMode ? 'border-slate-800' : 'border-white'}`}
                />
                <img
                  src="/assets/img/avatars/avatar2.png"
                  alt="User avatar center"
                  className={`absolute left-1/2 h-16 w-16 -translate-x-1/2 rounded-full border-4 object-cover shadow-lg ${isDarkMode ? 'border-slate-800' : 'border-white'}`}
                />
                <img
                  src="/assets/img/avatars/avatar3.png"
                  alt="User avatar right"
                  className={`absolute right-0 h-10 w-10 rounded-full object-cover border-2 shadow-md ${isDarkMode ? 'border-slate-800' : 'border-white'}`}
                />
              </div>
             
            </div>

            <div className="relative z-10 overflow-hidden pb-2 md:pb-3">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black/45 to-transparent md:w-32"></div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black/45 to-transparent md:w-32"></div>
              <div
                className="animate-eco-scroll flex w-max items-center gap-8 md:gap-16"
                style={{ animationDuration: `${marqueeDurationSec}s` }}
                aria-label="Live news marquee"
              >
                {[0, 1].map((copyIndex) => (
                  <div
                    key={`news-copy-${copyIndex}`}
                    ref={copyIndex === 0 ? marqueeContentRef : null}
                    className="flex items-center gap-8 md:gap-16"
                    aria-hidden={copyIndex === 1}
                  >
                    {newsItems.map((item, index) => (
                      <a
                        key={`news-${copyIndex}-${index}`}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whitespace-nowrap text-base font-semibold text-white hover:text-white/80 transition-colors duration-300 md:text-lg flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-white/60"></span>
                        {item.title}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>

          </div>

          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

            .liquid-glass {
              background: rgba(255, 255, 255, 0.01);
              background-blend-mode: luminosity;
              backdrop-filter: blur(4px);
              -webkit-backdrop-filter: blur(4px);
              border: none;
              box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.1);
              position: relative;
              overflow: hidden;
            }

            .liquid-glass::before {
              content: '';
              position: absolute;
              inset: 0;
              border-radius: inherit;
              padding: 1.4px;
              background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.45) 0%,
                rgba(255, 255, 255, 0.15) 20%,
                rgba(255, 255, 255, 0) 40%,
                rgba(255, 255, 255, 0) 60%,
                rgba(255, 255, 255, 0.15) 80%,
                rgba(255, 255, 255, 0.45) 100%
              );
              -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
              -webkit-mask-composite: xor;
              mask-composite: exclude;
              pointer-events: none;
            }

            @keyframes scroll {
              0% {
                transform: translateX(0);
              }

              100% {
                transform: translateX(-50%);
              }
            }

            .animate-eco-scroll {
              animation: scroll linear infinite;
              will-change: transform;
            }
          `}</style>
        </section>

      {/* Image Gallery Section - Merged with Hero */}
      <section id="image-gallery" className={`pt-2 pb-8 transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-4">
            <h2 className={`text-3xl lg:text-4xl font-extrabold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              For the Energy Leaders
            </h2>
          </div>

          {/* Clean Grid Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 - Data Ingestion */}
            <div 
              className={`group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${isDarkMode ? 'border border-slate-700/60 shadow-slate-950/40' : ''}`}
              onClick={() => navigate('/service/data-ingestion')}
              style={{ height: '285px' }}
            >
              <img 
                src="/assets/img/general/steptodown.com541656.jpg" 
                alt="Data Ingestion"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="text-lg font-extrabold mb-1 text-white">Data Ingestion</h3>
                <p className="text-xs text-white font-extrabold line-clamp-2">Leveraging communication protocols like OPC-DA/UA, MODBUS-TCIP/IP, HTTP/S, MQTT, Kafka, and REST APIs for data acquisition from Sensors and IOT Devices.</p>
              </div>
            </div>

            {/* Card 2 - Asset Monitoring */}
            <div 
              className={`group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${isDarkMode ? 'border border-slate-700/60 shadow-slate-950/40' : ''}`}
              onClick={() => navigate('/service/asset-performance-monitoring')}
              style={{ height: '285px' }}
            >
              <img 
                src="/assets/img/general/photovoltaic-2814504_1280.jpg" 
                alt="Asset Monitoring"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="text-lg font-extrabold mb-1 text-white">Asset Monitoring</h3>
                <p className="text-xs text-white font-extrabold">Monitor and assess portfolio health. Identify root-cause for technical solutions.</p>
              </div>
            </div>

            {/* Card 3 - Advanced Analytics */}
            <div 
              className={`group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${isDarkMode ? 'border border-slate-700/60 shadow-slate-950/40' : ''}`}
              onClick={() => navigate('/service/solar-advanced-analytics')}
              style={{ height: '285px' }}
            >
              <img 
                src="/assets/img/general/steptodown.com799996.jpg" 
                alt="Advanced Analytics"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="text-lg font-extrabold mb-1 text-white">Advanced Analytics</h3>
                <p className="text-xs text-white font-extrabold">Assured performance improvement of operational renewable energy assets.</p>
              </div>
            </div>

            {/* Card 4 - Repowering */}
            <div 
              className={`group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${isDarkMode ? 'border border-slate-700/60 shadow-slate-950/40' : ''}`}
              onClick={() => navigate('/service/wind-repowering')}
              style={{ height: '285px' }}
            >
              <img 
                src="/assets/img/general/steptodown.com921453.jpg" 
                alt="Repowering"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="text-lg font-extrabold mb-1 text-white">Repowering</h3>
                <p className="text-xs text-white font-extrabold">Re-powering of stressed assets to meet PPA Limits</p>
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* Leading the Future of Renewable Energy Section - Redesigned */}
      <section className={`relative py-4 lg:py-6 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        {/* Background decorative elements */}
        <div className="hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-1 relative z-10">
          <div className="text-center mb-1 lg:mb-1">
            <h2 className={`text-3xl lg:text-4xl font-extrabold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Leading the Future of Renewable Energy
            </h2>
          </div>
          <div className="text-center mb-1 lg:mb-1">
            <h3 className={`text-1xl lg:text-1xl font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
               Discover why global enterprises trust AxonMetric for their renewable energy optimization and management needs
            </h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center mt-14">
            {/* Left Column - RTC GIF */}
            <motion.div
              className="relative h-full flex items-center"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative w-full max-h-[500px] flex items-center justify-center">
                {/* RTC GIF Container */}
                <div className="relative rounded-2xl overflow-hidden bg-transparent">
                  <img 
                    src="/assets/img/RTC.gif" 
                    alt="Real-Time Control Technology"
                    className="w-full h-auto object-contain max-h-[400px] bg-transparent"
                    style={{ background: 'transparent' }}
                  />
                </div>

                {/* Floating badge */}
                <div className={`absolute top-1 mt-[-40px] right-30 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg z-10 ${isDarkMode ? 'bg-slate-800/90 border border-slate-700 text-white' : 'bg-white/95 text-gray-900'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Round-The-Clock RE Power</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Content */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Key Points */}
              <motion.div
                className="space-y-3 pt-2"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div>
                  <motion.h2
                  className={`text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                 Round-The-Clock (RTC) Renewable Power
                  <br />
                </motion.h2>
                  <p className={`text-sm font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`} style={{ fontWeight: '900' }}>Resource Optimization Algorithm for Round-The-Clock renewable energy meeting time-block Load Factor.</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Grid Stabilization Section */}
      <section className={`relative py-4 lg:py-6 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        {/* Background decorative elements */}
        <div className="hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left Column - Content */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-3">
                <motion.h2
                  className={`text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  Grid Stabilization
                  <br />
                </motion.h2>
                
                <motion.p
                  className={`text-base lg:text-lg font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Empowering renewable energy integration with cutting-edge grid stabilization technologies for reliable and efficient power distribution.
                </motion.p>
              </div>

              {/* Key Points */}
              <motion.div
                className="space-y-3 pt-2"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div>
                  <h3 className={`text-base font-extrabold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Grid Stabilization</h3>
                  <p className={`text-sm font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`} style={{ fontWeight: '900' }}>Reactive Compensation, Power Curtailment, TRAS Analysis for Capacity Utilization enhancement and Grid stabilization.</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Column - Solar Farm Image */}
            <motion.div
              className="relative h-full flex items-center"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative w-full max-h-[500px] flex items-center justify-center">
                {/* Image Container */}
                <div className={`relative rounded-2xl overflow-hidden shadow-xl ${isDarkMode ? 'border border-slate-700/80 shadow-2xl shadow-slate-950/60' : 'bg-white'}`}>
                  <img 
                    src="/assets/img/general/steptodown.com430799.jpg" 
                    alt="Advanced Grid Stabilization Solutions"
                    className="w-full h-auto object-cover max-h-[500px]"
                  />
                </div>

                {/* Floating badge */}
                <div className={`absolute top-4 right-4 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg z-10 ${isDarkMode ? 'bg-slate-800/90 border border-slate-700 text-white' : 'bg-white/95 text-gray-900'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Grid Stabilization</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Energy Trading Section */}
      <section className={`relative py-4 lg:py-6 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        {/* Background decorative elements */}
        <div className="hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left Column - Image */}
            <motion.div
              className="relative h-full flex items-center"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative w-full max-h-[500px] flex items-center justify-center">
                {/* Image Container */}
                <div className={`relative rounded-2xl overflow-hidden shadow-xl ${isDarkMode ? 'border border-slate-700/80 shadow-2xl shadow-slate-950/60' : 'bg-white'}`}>
                  <img 
                    src="/assets/img/new-image/steptodown.com717722.jpg" 
                    alt="Energy Trading"
                    className="w-full h-auto object-cover max-h-[500px]"
                  />
                </div>

                {/* Floating badge */}
                <div className={`absolute top-4 right-4 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg z-10 ${isDarkMode ? 'bg-slate-800/90 border border-slate-700 text-white' : 'bg-white/95 text-gray-900'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Energy Trading</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Content */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-3">
                <motion.h2
                  className={`text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  Energy Trading
                </motion.h2>
                
                <motion.p
                  className={`text-base lg:text-lg font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Intelligent trading platform for Energy Arbitrage, hedging against price volatility and managing risks.
                </motion.p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section className={`relative py-4 lg:py-6 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        {/* Background decorative elements */}
        <div className="hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left Column - Content */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-3">
                <motion.h2
                  className={`text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  Expert Team
                </motion.h2>
                
                <motion.p
                  className={`text-base lg:text-lg font-extrabold leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Deep-domain expertise in renewable energy with decades of hands-on experience in Performance Analytics and Operations & Maintenance.
                </motion.p>
              </div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div
              className="relative h-full flex items-center"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative w-full max-h-[500px] flex items-center justify-center">
                {/* Image Container */}
                <div className={`relative rounded-2xl overflow-hidden shadow-xl ${isDarkMode ? 'border border-slate-700/80 shadow-2xl shadow-slate-950/60' : 'bg-white'}`}>
                  <img 
                    src="/assets/img/new-image/steptodown.com411728.jpg" 
                    alt="Expert Team"
                    className="w-full h-auto object-cover max-h-[500px]"
                  />
                </div>

                {/* Floating badge */}
                <div className={`absolute top-4 right-4 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg z-10 ${isDarkMode ? 'bg-slate-800/90 border border-slate-700 text-white' : 'bg-white/95 text-gray-900'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Expert Team</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Performance & Value Unlock Section - Redesigned */}
      {/* COMMENTED OUT - We'll see when and how to use it later */}
      {/* 
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-white via-gray-50/50 to-white overflow-hidden">
        Background decorative elements
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-72 h-72 bg-cyan-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Main Heading Section */}
          {/* <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Unlock the Performance and Value
              <br />
              <span style={{color: '#1C4D88'}}>of Renewable Energy Assets</span>
            </h2>
            <p className="text-lg lg:text-xl text-gray-900 font-extrabold max-w-4xl mx-auto leading-relaxed">
              Advanced Analytics & AI-Driven Insights designed for wind, solar, and battery storage systems to help bridge the gap between expected and actual energy output
            </p>
          </motion.div>

          {/* Three Column Feature Cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {/* Wind Energy Solutions */}
            {/* <motion.div
              className="group relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Decorative gradient overlay */}
              {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Icon Container */}
              {/* <div className="relative z-10 mb-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Wind className="w-8 h-8 text-white" />
                </div>
              </div>
              
              {/* Content */}
              {/* <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Wind Energy Solutions</h3>
                <p className="text-blue-100 leading-relaxed">Advanced monitoring and optimization for wind farms</p>
              </div>

              {/* Decorative corner element */}
              {/* <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/5 rounded-tl-full"></div>
            </motion.div>

            {/* Energy Analytics */}
            {/* <motion.div
              className="group relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Decorative gradient overlay */}
              {/* <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Icon Container */}
              {/* <div className="relative z-10 mb-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Activity className="w-8 h-8 text-white" />
                </div>
              </div>
              
              {/* Content */}
              {/* <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">Energy Analytics</h3>
                <p className="text-green-100 leading-relaxed">Real-time monitoring and performance optimization</p>
              </div>

              {/* Decorative corner element */}
              {/* <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/5 rounded-tl-full"></div>
            </motion.div>

            {/* Advanced Analytics & AI */}
            {/* <motion.div
              className="group relative bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {/* Decorative gradient overlay */}
              {/* <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Icon Container */}
              {/* <div className="relative z-10 mb-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>
              </div>
              
              {/* Content */}
              {/* <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-3 text-white">AI-Driven Insights</h3>
                <p className="text-purple-100 leading-relaxed">Bridging the gap between expected and actual energy output</p>
              </div>

              {/* Decorative corner element */}
              {/* <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/5 rounded-tl-full"></div>
            </motion.div>
          </div>

        </div>
      </section>
      */}


      {/* Portfolio Gallery Section - COMMENTED OUT FOR FUTURE USE */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Renewable Energy Projects Worldwide
            </h2>
            <p className="text-xl text-black font-bold max-w-3xl mx-auto">
              Explore our diverse portfolio of successful renewable energy installations and optimization projects
            </p>
          </div>
          
          {/* Portfolio Grid */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Project 1 - Solar Farm */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/photovoltaic-system-2742302_1280.jpg" 
                alt="Large Scale Solar Farm"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Mega Solar Installation</h3>
                <p className="text-sm text-gray-200 mb-3">500MW+ Solar Farm with AI-driven optimization</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-1 rounded text-white" style={{backgroundColor: '#1C4D88'}}>Solar</span>
                  <span className="text-xs">+25% Efficiency</span>
                </div>
              </div>
                  </div>
                  
            {/* Project 2 - Wind Farm */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/windmills-1838788_1280.jpg" 
                alt="Wind Farm Project"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Offshore Wind Farm</h3>
                <p className="text-sm text-gray-200 mb-3">200MW Wind Installation with predictive maintenance</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-1 rounded" style={{backgroundColor: '#1C4D88'}}>Wind</span>
                  <span className="text-xs">99.8% Uptime</span>
                </div>
              </div>
            </div>

            {/* Project 3 - Hybrid Installation */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/solar-2376233_1280.jpg" 
                alt="Hybrid Energy Installation"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Hybrid Solar-Wind</h3>
                <p className="text-sm text-gray-200 mb-3">Integrated renewable energy solution with storage</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-purple-600 px-2 py-1 rounded">Hybrid</span>
                  <span className="text-xs">24/7 Output</span>
                </div>
              </div>
            </div>

            {/* Project 4 - Rooftop Solar */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/photovoltaic-4541308_1280.jpg" 
                alt="Commercial Rooftop Solar"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Rooftop</h3>
                <p className="text-sm text-gray-200 mb-3">Urban solar deployment with smart grid integration</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-orange-600 px-2 py-1 rounded">Commercial</span>
                  <span className="text-xs">Smart Grid</span>
                </div>
              </div>
            </div>

            {/* Project 5 - Energy Storage */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/solar-cells-4518932_1280.jpg" 
                alt="Energy Storage System"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Battery Storage Hub</h3>
                <p className="text-sm text-gray-200 mb-3">Advanced energy storage with grid stabilization</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Storage</span>
                  <span className="text-xs">Grid Stable</span>
                </div>
              </div>
            </div>

            {/* Project 6 - Floating Solar */}
            {/* <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <img 
                src="/assets/img/general/solar-8244680_1280.jpg" 
                alt="Floating Solar Farm"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2 text-white">Floating Solar Array</h3>
                <p className="text-sm text-gray-200 mb-3">Innovative water-based solar installation</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-cyan-600 px-2 py-1 rounded">Floating</span>
                  <span className="text-xs">Water Cooled</span>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Stats */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>500+</div>
              <div className="text-black font-bold">Projects Completed</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>5GW+</div>
              <div className="text-black font-bold">Total Capacity</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>30+</div>
              <div className="text-black font-bold">Countries</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold text-orange-600 mb-2">99.9%</div>
              <div className="text-black font-bold">Uptime Rate</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Professional CTA Section */}
      <section id="expert-consultation" className={`py-12 lg:py-16 relative overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        {/* Professional Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 lg:px-6 relative z-10">
          <div className={`backdrop-blur-xl rounded-3xl shadow-2xl border p-4 sm:p-6 lg:p-8 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-[#131D31]/95 border-slate-700/60 shadow-slate-950/50' 
              : 'bg-white/95 border-white/20'
          }`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 xl:gap-12 items-center">
              
              {/* Left Content */}
              <div className="space-y-4 lg:space-y-6">
                <div className="space-y-3">
                  <h2 className={`text-lg sm:text-xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Get Expert Consultation from 
                    <span style={{color: '#eb0200'}}> AxonMetric</span>
                    <br />Renewable Energy Specialists
                  </h2>
                  
                  <p className={`text-base lg:text-lg font-extrabold leading-relaxed max-w-2xl ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
                    Partner with our innovative renewable energy platform featuring cutting-edge analytics, AI-driven insights, and advanced optimization strategies designed to maximize your energy portfolio performance.
                  </p>
                </div>

                {/* Key Benefits */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                    isDarkMode ? 'bg-[#0B1120] border-cyan-500/40 text-white' : 'bg-gray-50 border-[#1C4D88]'
                  }`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#1C4D88'}}>
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className={`font-extrabold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>AI-Powered Analytics</h4>
                      <p className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-gray-900'}`}>Advanced optimization algorithms</p>
                    </div>
                  </div>
                  
                  <div className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                    isDarkMode ? 'bg-[#0B1120] border-cyan-500/40 text-white' : 'bg-gray-50 border-[#1C4D88]'
                  }`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#1C4D88'}}>
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className={`font-extrabold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Secure Platform</h4>
                      <p className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-gray-900'}`}>Enterprise-grade security</p>
                    </div>
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center gap-6 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#1C4D88'}}>
                      <span className="text-white text-sm font-bold">AI</span>
                    </div>
                    <span className={`text-sm font-extrabold ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>AI-Driven Solutions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#1C4D88'}}>
                      <span className="text-white text-sm font-bold">24/7</span>
                    </div>
                    <span className={`text-sm font-extrabold ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Continuous Monitoring</span>
                  </div>
                </div>
              </div>
              
              {/* Right Form */}
              <div className={`rounded-2xl p-4 sm:p-6 shadow-xl border transition-colors ${
                isDarkMode 
                  ? 'bg-[#0F172A] border-slate-700/80' 
                  : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
              }`}>
                <div className="space-y-3 sm:space-y-4">
                  <div className="text-center">
                    <h3 className={`text-lg sm:text-xl font-extrabold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Schedule Your Demo</h3>
                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-400' : 'text-gray-900'}`}>Get a personalized consultation with our renewable energy experts</p>
                  </div>
                  
                  <form className="space-y-4" onSubmit={handleFormSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="firstName" className={`block text-sm font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>First Name</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="John"
                          required
                          className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 ${
                            isDarkMode 
                              ? 'bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:ring-cyan-500' 
                              : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500'
                          }`}
                          style={!isDarkMode ? {focusRingColor: '#1C4D88'} : undefined}
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className={`block text-sm font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Last Name</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          placeholder="Doe"
                          required
                          className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 ${
                            isDarkMode 
                              ? 'bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:ring-cyan-500' 
                              : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500'
                          }`}
                          style={!isDarkMode ? {focusRingColor: '#1C4D88'} : undefined}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="email" className={`block text-sm font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Business Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="john@company.com"
                        required
                        className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:ring-cyan-500' 
                            : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500'
                        }`}
                        style={!isDarkMode ? {focusRingColor: '#1C4D88'} : undefined}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className={`block text-sm font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Your Company Name"
                        required
                        className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:ring-cyan-500' 
                            : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500'
                        }`}
                        style={!isDarkMode ? {focusRingColor: '#1C4D88'} : undefined}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="energyCapacity" className={`block text-sm font-extrabold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Energy Portfolio Size</label>
                      <select
                        id="energyCapacity"
                        name="energyCapacity"
                        required
                        className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-slate-900 border border-slate-700 text-white focus:ring-cyan-500' 
                            : 'bg-white border border-gray-200 text-gray-900'
                        }`}
                        style={!isDarkMode ? {focusRingColor: '#1C4D88'} : undefined}
                      >
                        <option value="" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>Select portfolio size</option>
                        <option value="0-10MW" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>0-10 MW</option>
                        <option value="10-50MW" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>10-50 MW</option>
                        <option value="50-100MW" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>50-100 MW</option>
                        <option value="100-500MW" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>100-500 MW</option>
                        <option value="500MW+" className={isDarkMode ? 'bg-slate-900 text-white' : ''}>500+ MW</option>
                      </select>
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full text-white font-extrabold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{backgroundColor: isDarkMode ? '#0084CF' : '#1C4D88'}}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending Request...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4" />
                          Schedule Free Consultation
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                    
                    <p className={`text-xs font-semibold text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-900'}`}>
                      By submitting this form, you agree to our privacy policy. No spam, ever.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials & Success Stories Section - COMMENTED OUT */}
      {/* <section className="py-20" style={{background: 'linear-gradient(to bottom right, #1C4D88, #1C4D88)', opacity: 0.05}}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-black font-bold max-w-3xl mx-auto">
              See how we've helped organizations achieve remarkable improvements in their renewable energy operations
            </p>
          </div>

          {/* Testimonials Grid */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Testimonial 1 */}
            {/* <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4 mb-6">
                <img 
                  src="/assets/img/general/steptodown.com151755.jpg" 
                  alt="Energy Executive"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Sarah Chen</h4>
                  <p className="text-sm text-black font-bold">CTO, GreenTech Energy</p>
                  <div className="flex items-center mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-black font-bold leading-relaxed">
                "AxonMetric's AI-driven analytics transformed our solar farm operations. We've seen a 28% increase in energy efficiency and reduced maintenance costs by 40%. Their predictive maintenance capabilities are game-changing."
                    </p>
                  </div>

            {/* Testimonial 2 */}
            {/* <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4 mb-6">
                <img 
                  src="/assets/img/general/steptodown.com188081.jpg" 
                  alt="Operations Manager"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Michael Rodriguez</h4>
                  <p className="text-sm text-black font-bold">Operations Director, WindFlow Corp</p>
                  <div className="flex items-center mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                </div>
                </div>
              </div>
              <p className="text-black font-bold leading-relaxed">
                "The real-time monitoring and automated workflows have revolutionized how we manage our wind portfolio. Downtime reduced by 60% and we're generating 15% more energy than projected."
              </p>
            </div>

            {/* Testimonial 3 */}
            {/* <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4 mb-6">
                <img 
                  src="/assets/img/general/steptodown.com274154.jpg" 
                  alt="Sustainability Officer"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Emma Thompson</h4>
                  <p className="text-sm text-black font-bold">Chief Sustainability Officer, EcoPower</p>
                  <div className="flex items-center mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-black font-bold leading-relaxed">
                "AxonMetric's comprehensive platform gave us the insights we needed to optimize our entire renewable energy portfolio. ROI improved by 35% in the first year alone."
              </p>
            </div>
          </div>

          {/* Success Metrics */}
          {/* <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Results Across the Industry</h3>
              <p className="text-black font-bold">Real impact delivered to renewable energy operators worldwide</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>+35%</div>
                <div className="text-sm text-black font-bold">Average ROI Improvement</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>-45%</div>
                <div className="text-sm text-black font-bold">Reduction in Downtime</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{color: '#1C4D88'}}>+28%</div>
                <div className="text-sm text-black font-bold">Energy Efficiency Gain</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">24/7</div>
                <div className="text-sm text-black font-bold">Continuous Monitoring</div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      
      {/* Footer Section */}
      <Footer footer={6} />
      </div>

    </Layout>
  );
};

export default NewHomePage;
